<template>
    <FormsCard>
        <img class="block mx-auto mb-6 w-32 sm:w-40 h-auto" src="@/assets/omind_logo.png" alt="test">
        <h2 class="text-center pb-5 text-xl sm:text-2xl">
            {{ $t('LoginComponent.welcomeMessage') }}</h2>
        <Form  v-if="manualLoginEnabled" :validateOnValueUpdate="false" :resolver="resolver"
            :validateOnBlur="true" @submit="login" class="flex flex-col gap-4 w-full md:shrink-0">
            <FormField v-slot="$field" as="section" name="email"
                initialValue="" class="flex flex-col gap-1 ">
                <InputText type="text" size="large" :invalid="$field?.invalid"
                    :placeholder="$t('LoginComponent.placeholders.enterEmail')"
                    id="txt_mail" data-testid="txt_email"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="password"
                initialValue="" class="flex flex-col gap-1">
                <Password type="text" :feedback="false" toggleMask fluid size="large"
                    :invalid="$field?.invalid" id="txt_password" data-testid="txt_password"
                    :placeholder="$t('LoginComponent.placeholders.password')"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <div class="flex flex-row">
                <LanguageComponent class="float-left mt-1 absolute"/>
                <div class="ml-auto text-[var(--color-orange)] text-lg font-bold py-2 px-2">
                    <router-link to="/auth/password-reset"
                        class="transition duration-500 hover:text-[var(--color-fulvous)]">
                        {{ $t('LoginComponent.forgotPassword') }}
                    </router-link>
                </div>
            </div>
            <Button type="submit" :disabled="isSubmitting" v-if="manualLoginEnabled"
                class="btn-primary-accent mt-8 sm:mt-12 w-full sm:w-auto px-6 py-2.5 sm:px-12 sm:py-3 text-lg sm:text-2xl mx-auto block text-center" unstyled
                id="btn_login" data-testid="btn_login">
                {{ $t('LoginComponent.login') }}
            </Button>
            <div class="text-center pt-4 text-lg">
                {{ $t('LoginComponent.dontHaveAnAccount') }}
                <router-link to="/auth/register" @getLocaleErrorMessage="getLocaleErrorMessage"
                    class=" text-[var(--color-orange)] transition duration-500
                    hover:text-[var(--color-fulvous)] font-bold"
                    data-testid="link_signUp">
                    {{ $t('LoginComponent.signup') }}
                </router-link>
            </div>
            <Button v-if="SSOLoginEnabled" type="button" :disabled="isSubmitting"
                @click="SSOlogin()" id="btn_login" icon="pi pi-microsoft"
                class="btn-primary-accent mt-8 sm:mt-12 w-full sm:w-auto px-6 py-2.5 sm:px-12 sm:py-3 text-lg sm:text-2xl mx-auto block"
                :label="$t('LoginComponent.login')" unstyled/>
        </Form>
    </FormsCard>
  </template>

<script>
import LanguageComponent from '@/components/global/LanguageComponent.vue';
import keycloak from '../../../keycloak';
import FormsCard from '../global/slots/FormsCard.vue';
import { Form, FormField } from '@primevue/forms';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Password from 'primevue/password';
import Button from 'primevue/button';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { TENANT_ROLE_ADMIN } from '@/services/enumService';

export default {
  name: 'LoginComponent',
  components: {
    LanguageComponent,
    FormsCard,
    Form,
    FormField,
    InputText,
    Message,
    Password,
    Button,
  },
  data() {
    return {
      isSubmitting: false,
    };
  },
  computed: {
    resolver() {
      return zodResolver(
        z.object({
          email: z.string()
            .min(1, { message: this.$t('LoginComponent.validation.emailRequired') })
            .email({ message: this.$t('LoginComponent.validation.emailFormat') }),
          password: z.string().min(1, { message: this.$t('LoginComponent.validation.passwordRequired') }),
        }),
      );
    },
    SSOLoginEnabled() {
      return import.meta.env.VITE_KEYCLOAK === 'true';
    },
    manualLoginEnabled() {
      return import.meta.env.VITE_MANUAL_REGISTRATION === 'true';
    },
  },
  methods: {
    async SSOlogin() {
      this.isSubmitting = true;
      await keycloak.login({ idpHint: 'oidc' });
      this.isSubmitting = false;
    },
    async login(values) {
      if (values.valid) {
        try {
          this.isSubmitting = true;
          await this.$store.dispatch('login', values.values);
          if (this.$store.getters.isLoggedIn) {
            // If a redirect URL was passed (e.g. from assessment link), go there
            const { redirect } = this.$route.query;
            if (redirect) {
              this.$router.push(redirect);
            } else if ((this.$store.getters.getUserRoles || []).includes(TENANT_ROLE_ADMIN)) {
              // Check if user is admin and redirect to content management
              this.$router.push('/contentManagement');
            } else {
              this.$router.push('/');
            }
          }
        } catch (error) {
          this.isSubmitting = false;
          // eslint-disable-next-line
          const errorMessage = await this.getLocaleErrorMessage(error);
          // Todo handle errors
          this.$toast.add({
            severity: 'error',
            summary: errorMessage,
            detail: '',
            life: 3000,
          });
        }
      }
    },
    async getLocaleErrorMessage(error) {
      console.log(error);
      const errorCode = error?.response?.data?.code;
      let toastErrorMessage;
      switch (errorCode) {
        case 40001:
          toastErrorMessage = this.$t('LoginComponent.toast.wrongCredentials');
          break;
        case 40004:
          // FA-11.11: distinct from wrong password; points user to registration
          toastErrorMessage = this.$t('LoginComponent.toast.unknownEmail');
          break;
        case 40005:
          toastErrorMessage = this.$t(
            'LoginComponent.toast.wrongAuthenticationToken',
          );
          break;
        case 40006:
          toastErrorMessage = this.$t(
            'LoginComponent.toast.invalidUser',
          );
          break;
        case 40007:
          toastErrorMessage = this.$t(
            'LoginComponent.toast.invalidLoginInformation',
          );
          break;
        case 40013:
          toastErrorMessage = this.$t(
            'LoginComponent.toast.userNotValidated',
          );
          break;
        default:
          toastErrorMessage = this.$t('LoginComponent.toast.wrongCredentials');
      }
      return toastErrorMessage;
    },
  },
};
</script>
